import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import AsyncSelect from "react-select/async";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";

import { Box, Button, FormControlLabel, Switch } from "@mui/material";
import TextField from "@mui/material/TextField";
import { api } from "../../../utils/api";
import ModalPopup from "../../../components/ModalPopup";
import {
  ADMIN_MANUAL_SERVICE_GROUP_LABEL,
  isAdminManualServiceProduct,
  productRequiresVehicleVariant
} from "../../../utils/adminManualServiceProducts";
import {
  duplicateGuardModalTitle,
  isDuplicateGuardBlockPayload,
} from "../../../utils/redemptionDuplicateGuardUi";

// Build grouped product options by type (add-ons grouped together after subscriptions)
const buildProductOptionsGrouped = (allProducts) => {
  const addOnProducts = [];
  const subscription = [];
  const packageType = [];
  const onceoff = [];
  allProducts.forEach((p) => {
    const isManualService = isAdminManualServiceProduct(p);
    const option = {
      value: p._id,
      label: p.name,
      type: p.type,
      isAddOnOnly: !!p.isAddOnOnly,
      isAdminManualService: isManualService,
      requiresVehicleVariant: productRequiresVehicleVariant(p)
    };
    if (isManualService || p.isAddOnOnly) {
      addOnProducts.push(option);
    } else if (p.type === "subscription") {
      subscription.push(option);
    } else if (p.type === "package") {
      packageType.push(option);
    } else {
      onceoff.push(option);
    }
  });
  const groups = [];
  if (subscription.length) groups.push({ label: "Subscription", options: subscription });
  if (addOnProducts.length) {
    groups.push({ label: ADMIN_MANUAL_SERVICE_GROUP_LABEL, options: addOnProducts });
  }
  if (packageType.length) groups.push({ label: "Package", options: packageType });
  if (onceoff.length) groups.push({ label: "One-off", options: onceoff });
  return groups;
};

const RedemptionForm = ({ selectedRedemption, callback, closeModal }) => {
  const today = new Date();
  const [productOptions, setProductOptions] = useState([]);
  const [productOptionsGrouped, setProductOptionsGrouped] = useState([]);
  const [product, setProduct] = useState("");
  const [addOnPriceVariant, setAddOnPriceVariant] = useState("");
  const [vehicleImage, setVehicleImage] = useState();

  const ADDON_PRICE_VARIANTS = [
    { value: "sedan", label: "Sedan / Hatch" },
    { value: "wagon", label: "SUV / Wagon" },
    { value: "4wd", label: "4WD / 7 Seater" },
  ];

  const [merchantAccountOptions, setMerchantAccountOptions] = useState([]);
  const [merchantAccount, setMerchantAccount] = useState("");
  const [merchantStoreOptions, setMerchantStoreOptions] = useState([]);
  const [merchantStore, setMerchantStore] = useState("");
  const [merchantUserOptions, setMerchantUserOptions] = useState([]);
  const [merchantUser, setMerchantUser] = useState("");

  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [vehicle, setVehicle] = useState("");
  const [active, setActive] = useState("");
  // Contract validation popup for merchants
  const [contractValidationModalOpen, setContractValidationModalOpen] = useState(false);
  const [contractValidationMessage, setContractValidationMessage] = useState("");

  // Admin override popup for missing active subscriptions
  const [subscriptionOverrideModalOpen, setSubscriptionOverrideModalOpen] = useState(false);
  const [subscriptionOverrideError, setSubscriptionOverrideError] = useState(null);
  const [subscriptionOverrideLoading, setSubscriptionOverrideLoading] = useState(false);
  const [pendingSubscriptionOverrideValues, setPendingSubscriptionOverrideValues] = useState(null);

  const [duplicateGuardModalOpen, setDuplicateGuardModalOpen] = useState(false);
  const [duplicateGuardError, setDuplicateGuardError] = useState(null);
  const [duplicateGuardReason, setDuplicateGuardReason] = useState("");
  const [duplicateGuardLoading, setDuplicateGuardLoading] = useState(false);
  const [pendingDuplicateGuardValues, setPendingDuplicateGuardValues] = useState(null);

  const isAdminPortal = sessionStorage.getItem("selectedPortal") === "Administrator";

  const buildRedemptionFormData = (
    values,
    { overrideSubscriptionNotFound = false, duplicateGuardOverrideReason = "" } = {}
  ) => {
    const formData = new FormData();

    formData.append("vehicle", vehicle?.value || null);
    formData.append("product", product?.value || null);

    if (product?.requiresVehicleVariant && addOnPriceVariant) {
      formData.append("vehicleType", addOnPriceVariant);
    }

    formData.append("merchantStore", merchantStore?.value || null);
    formData.append("merchantUser", merchantUser?.value || null);
    formData.append("redemptionDateUTC", values.redemptionDateUTC);
    formData.append("active", values.active);

    if (vehicleImage) {
      formData.append("vehicleImage", vehicleImage);
    }

    const selectedPortal = sessionStorage.getItem("selectedPortal");
    if (selectedPortal) {
      formData.append("selectedPortal", selectedPortal);
    }

    if (overrideSubscriptionNotFound) {
      formData.append("overrideSubscriptionNotFound", "true");
    }

    if (duplicateGuardOverrideReason) {
      formData.append("duplicateGuardOverrideReason", duplicateGuardOverrideReason);
    }

    return formData;
  };

  const getAllProducts = async () => {
    try {
      const response = await api("v1/product?includeAll=true", "GET", null);
      if (response.data.success) {
        const allProducts = response.data.data.allProducts;
        const result = allProducts.map((p) => ({
          value: p._id,
          label: p.name,
          type: p.type,
          isAddOnOnly: !!p.isAddOnOnly,
          isAdminManualService: isAdminManualServiceProduct(p),
          requiresVehicleVariant: productRequiresVehicleVariant(p)
        }));
        const grouped = buildProductOptionsGrouped(allProducts);
        if (result.length) {
          setProductOptions(result);
          setProductOptionsGrouped(grouped);
          if (selectedRedemption) {
            const id = selectedRedemption.product?._id ?? selectedRedemption.product?.value;
            const selectedProduct = result.find((p) => p.value === id);
            if (selectedProduct) setProduct(selectedProduct);
          }
        }
      }
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const getAllVehicles = async () => {
    try {
      const response = await api("v1/vehicle", "GET", null);
      if (response.data.success) {
        const allVehicles = response.data.data.allVehicles;
        const result = allVehicles.map((vehicle) => ({
          value: vehicle._id,
          label: `<div className="flex flex-column gap-0">
                                <p>Rego: ${vehicle.rego} </p>
                                <p>VIN: ${vehicle.vin}</p>                               
                                <p>Make: ${vehicle.make}</p>                               
                                <p>Model: ${vehicle.model}</p>                               
                                <p>Model Year: ${vehicle.modelYear}</p>                               
                                <p>Color: ${vehicle.color}</p>                               
                            </div>`
        }));

        if (result) {
          setVehicleOptions(result);
          console.log("Set Options for Vehicle Options", result);
          if (selectedRedemption) {
            const selectedVehicle = result.find((vehicle) => vehicle._id === selectedRedemption.vehicle.value);
            setVehicle(selectedVehicle);
          }
        }
      }
    } catch (error) {
      console.error("Error loading partner companies:", error);
    }
  };

  const getMerchantAccounts = async () => {
    try {
      const response = await api("v1/merchant", "GET", null);
      if (response.data.success) {
        console.log(response);
        const allMerchantAccounts = response.data.data.allMerchants;
        const result = allMerchantAccounts.map((merchantAccountItem) => ({
          value: merchantAccountItem._id,
          label: merchantAccountItem.name,
          stores: merchantAccountItem.stores
        }));
        // console.log("SELECTED STORE", selectedRedemption.merchantStore);
        if (result) {
          setMerchantAccountOptions(result);
          console.log("Set Options for MerchantAccount Options", result);
          if (selectedRedemption) {
            const selectedMerchantAccount = result.find((merchantAccountItem) => {
              // console.log("STORES", selectedRedemption.merchantStore);
              return merchantAccountItem.stores.some((store) => store._id === selectedRedemption.merchantStore?._id);
            });
            console.log("SELECTED MERCHANT", selectedMerchantAccount);
            setMerchantAccount(selectedMerchantAccount);
          }
        }
      }
    } catch (error) {
      console.error("Error loading merchant accounts:", error);
    }
  };

  const getMerchantStores = async () => {
    if (!merchantAccount) {
      return;
    }
    try {
      const response = await api("v1/merchant/" + merchantAccount.value + "/store", "GET", null);
      if (response.data.success) {
        const allMerchantStores = response.data.data.merchantStores;
        const result = allMerchantStores.map((merchantStore) => ({
          value: merchantStore._id,
          label: merchantStore.name
        }));

        if (result) {
          setMerchantStoreOptions(result);
          console.log("Set Options for Merchant Store Options", result);
          if (selectedRedemption) {
            const selectedMerchantStore = result.find((item) => item.value === selectedRedemption.merchantStore?._id);
            setMerchantStore(selectedMerchantStore);
          }
        } else {
          setMerchantStoreOptions([]);
        }
      }
    } catch (error) {
      console.error("Error loading merchant stores:", error);
    }
  };

  const getMerchantUsers = async () => {
    if (!merchantAccount) {
      return;
    }
    try {
      const response = await api("v1/merchant/" + merchantAccount.value + "/user", "GET", null);
      if (response.data.success) {
        const allMerchantUsers = response.data.data.merchantUsers;
        const result = allMerchantUsers.map((merchantUser) => ({
          value: merchantUser._id,
          label: merchantUser.firstName + " " + merchantUser.lastName + " (" + merchantUser.email + ")"
        }));

        if (result) {
          setMerchantUserOptions(result);
          console.log("Set Options for Merchant User", result);
          if (selectedRedemption) {
            const selectedMerchantUser = result.find((item) => item.value === selectedRedemption.merchantUser?._id);
            setMerchantUser(selectedMerchantUser);
          }
        } else {
          setMerchantUserOptions([]);
        }
      }
    } catch (error) {
      console.error("Error loading merchant users:", error);
    }
  };

  useEffect(() => {
    getAllProducts();
    getAllVehicles();
    getMerchantAccounts();
    getMerchantStores();
    getMerchantUsers();
  }, []);

  useEffect(() => {
    getMerchantStores();
    getMerchantUsers();
  }, [merchantAccount]);

  useEffect(() => {
    if (selectedRedemption?.vehicleType && ["sedan", "wagon", "4wd"].includes(selectedRedemption.vehicleType.toLowerCase())) {
      setAddOnPriceVariant(selectedRedemption.vehicleType.toLowerCase());
    }
  }, [selectedRedemption?.vehicleType]);

  // useEffect(() => {
  //     getMerchantUsers();
  // }, [merchantStore]);

  const checkContractAcceptance = async (subscriptionId, productType) => {
    try {
      // Only check for subscription-type products
      if (productType !== "subscription") {
        return { requiresContract: false };
      }

      const contractResponse = await api(`v1/contract/subscription/${subscriptionId}`, "GET");

      if (contractResponse.data.success) {
        const contractData = contractResponse.data.data.contract;
        const requiresContract = contractResponse.data.data.requiresContract;
        const isSigned = contractResponse.data.data.isSigned;

        return {
          requiresContract: requiresContract,
          isSigned: isSigned,
          contractStatus: contractData?.status || "unknown"
        };
      } else {
        // If API fails, assume contract is required but not signed
        return {
          requiresContract: true,
          isSigned: false,
          contractStatus: "unknown"
        };
      }
    } catch (error) {
      console.error("Error checking contract acceptance:", error);
      // If check fails, assume contract is required but not signed
      return {
        requiresContract: true,
        isSigned: false,
        contractStatus: "error"
      };
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Submitted Data", values);

    if (Object.keys(vehicle).length === 0 || Object.keys(product).length === 0) {
      // Having issue with YUP validation will use this method for now.
      alert("Required Fields not selected for vehicle or product");
      return false;
    }
    if (product?.requiresVehicleVariant && !addOnPriceVariant) {
      alert("Please select a price variant for this product (Sedan/Hatch, SUV/Wagon, or 4WD/7 Seater).");
      return false;
    }

    // Close any existing contract validation modal
    setContractValidationModalOpen(false);

    // Check if this is a new redemption (not editing existing one)
    if (!selectedRedemption) {
      // Find subscription for this vehicle and product to check contract
      try {
        // First, fetch vehicle details to get customer ID if not already available
        let customerId = vehicle.value?.customer;

        // If customer ID is not in vehicle.value, fetch vehicle details
        if (!customerId && vehicle.value?._id) {
          try {
            const vehicleResponse = await api(`v1/vehicle/${vehicle.value._id}`, "GET");
            if (vehicleResponse?.data?.success) {
              customerId = vehicleResponse.data.data.vehicle.customer;
            }
          } catch (vehicleError) {
            console.error("Error fetching vehicle details:", vehicleError);
          }
        }

        // If we still don't have customer ID, use subscription search instead
        let subscriptions = [];
        if (customerId) {
          const subscriptionResponse = await api(`v1/subscription/customer/${customerId}`, "GET");
          if (subscriptionResponse?.data?.success) {
            subscriptions = subscriptionResponse.data.data.allSubscriptions || [];
          }
        } else {
          // Fallback: search subscriptions by vehicle and product
          const subscriptionSearchResponse = await api(`v1/subscription/search`, "POST", {
            query: {
              vehicle: vehicle.value._id,
              product: product.value._id,
              active: true
            }
          });
          if (subscriptionSearchResponse?.data?.success) {
            subscriptions = subscriptionSearchResponse.data.data.searchResult || [];
          }
        }

        if (subscriptions.length > 0) {
          const matchingSubscription = subscriptions.find(
            (sub) =>
              (sub.vehicle?._id === vehicle.value._id || sub.vehicle === vehicle.value._id) &&
              (sub.product?._id === product.value._id || sub.product === product.value._id) &&
              sub.active === true
          );

          if (matchingSubscription) {
            // Check if contract acceptance is required and signed
            const contractCheck = await checkContractAcceptance(matchingSubscription._id, product.value.type);

            if (contractCheck.requiresContract && !contractCheck.isSigned) {
              // Show contract validation popup for merchants
              setContractValidationMessage(
                `Customer needs to sign their subscription contract before redeeming. ` +
                  `Please ask the customer to log into their customer portal and sign the contract. ` +
                  `Contract Status: ${contractCheck.contractStatus}`
              );
              setContractValidationModalOpen(true);
              setSubmitting(false);
              return;
            }
          } else {
            // No matching subscription found - close contract validation modal
            setContractValidationModalOpen(false);
          }
        } else {
          // Subscription lookup failed - close contract validation modal
          setContractValidationModalOpen(false);
        }
      } catch (error) {
        console.error("Error checking subscription for contract:", error);
        // Close contract validation modal on error
        setContractValidationModalOpen(false);
        // Continue with redemption if we can't check contract
      }
    }

    const formData = new FormData();
    // Send the actual ID values as strings, not objects
    // vehicle.value, product.value, etc. are already the _id strings from the select options
    formData.append("vehicle", vehicle.value || null);
    formData.append("product", product.value || null);
    if (product?.requiresVehicleVariant && addOnPriceVariant) {
      formData.append("vehicleType", addOnPriceVariant);
    }
    formData.append("merchantStore", merchantStore?.value || null);
    formData.append("merchantUser", merchantUser?.value || null);
    formData.append("redemptionDateUTC", values.redemptionDateUTC);
    formData.append("active", values.active);
    // Only append vehicleImage if it exists
    if (vehicleImage) {
      formData.append("vehicleImage", vehicleImage);
    }

    // Add selectedPortal to the request so backend knows if this is an admin request
    const selectedPortal = sessionStorage.getItem("selectedPortal");
    if (selectedPortal) {
      formData.append("selectedPortal", selectedPortal);
    }

    try {
      let response;
      if (selectedRedemption) {
        // Edit Redemption Option
        response = await api(
          `v1/redemption/${selectedRedemption._id}`,
          "PATCH",
          formData,
          {
            "Content-Type": "multipart/form-data" // Ensure proper headers for form data
          }
        );
      } else {
        // Add Redemption Option
        response = await api(`v1/redemption`, "POST", formData, {
          "Content-Type": "multipart/form-data" // Ensure proper headers for form data
        });
      }

      if (response.data.success) {
        var updatedData = null;

        if (selectedRedemption) {
          console.log("Redemption Updated: ", response.data.data.redemption);
          updatedData = response.data.data.redemption;
        } else {
          if (response.data.data.newRedemption) {
            console.log("New Redemption Created: ", response.data.data.newRedemption);
            updatedData = response.data.data.newRedemption;
          } else {
            alert(response.data.message);
          }
        }
        if (updatedData) {
          console.log(updatedData);

          const vehicle = await api(`v1/vehicle/${updatedData.vehicle}`, "GET");
          const product = await api(`v1/product/${updatedData.product}`, "GET");
          const merchantStore = updatedData.merchantStore
            ? await api(`v1/merchantStore/${updatedData.merchantStore}`, "GET")
            : null;
          const merchantUser = updatedData.merchantUser
            ? await api(`v1/merchantUser/${updatedData.merchantUser}`, "GET")
            : null;
          // console.log(updatedData);
          updatedData.vehicle = vehicle.data.data.vehicle;
          updatedData.product = product.data.data.product;
          updatedData.merchantStore = merchantStore ? merchantStore.data.data.merchantStore : null;
          updatedData.merchantUser = merchantUser ? merchantUser.data.data.merchantUser : null;
          callback(updatedData);
        }
      } else {
        const errorCode = response?.data?.errorCode;
        const errorDetails = response?.data?.details || "";

        if (
          isAdminPortal &&
          errorCode === "SUBSCRIPTION_NOT_FOUND" &&
          !formData.get("overrideSubscriptionNotFound")
        ) {
          setPendingSubscriptionOverrideValues(values);
          setSubscriptionOverrideError(response.data || { message: response?.data?.message });
          setSubscriptionOverrideModalOpen(true);
          setSubmitting(false);
          return;
        }

        if (isAdminPortal && isDuplicateGuardBlockPayload(response.data)) {
          setPendingDuplicateGuardValues(values);
          setDuplicateGuardError(response.data);
          setDuplicateGuardReason("");
          setDuplicateGuardModalOpen(true);
          setSubmitting(false);
          return;
        }

        // Contract acceptance removed - this is an admin form, not customer-facing
        const errorMessage =
          response?.data?.message || "Redemption Failed";
        const fullMessage = errorDetails
          ? `${errorMessage}\n\n${errorDetails}`
          : errorMessage;
        alert(fullMessage);
      }
    } catch (error) {
      console.error("API error:", error);
      const errorCode = error?.response?.data?.errorCode;
      const errorDetails = error?.response?.data?.details || "";

      if (
        isAdminPortal &&
        errorCode === "SUBSCRIPTION_NOT_FOUND"
      ) {
        setPendingSubscriptionOverrideValues(values);
        setSubscriptionOverrideError(error?.response?.data || null);
        setSubscriptionOverrideModalOpen(true);
        setSubmitting(false);
        return;
      }

      if (isAdminPortal && isDuplicateGuardBlockPayload(error?.response?.data)) {
        setPendingDuplicateGuardValues(values);
        setDuplicateGuardError(error.response.data);
        setDuplicateGuardReason("");
        setDuplicateGuardModalOpen(true);
        setSubmitting(false);
        return;
      }

      // Contract acceptance removed - this is an admin form, not customer-facing
      const errorMessage =
        error.response?.data?.message ||
        "Error creating redemption. Please try again.";
      const fullMessage = errorDetails
        ? `${errorMessage}\n\n${errorDetails}`
        : errorMessage;
      alert(fullMessage);
    }

    setSubmitting(false);
  };

  const handleDuplicateGuardOverride = async () => {
    const reason = duplicateGuardReason.trim();
    if (reason.length < 10) {
      alert("Please enter at least 10 characters explaining why this override is allowed.");
      return;
    }
    setDuplicateGuardLoading(true);
    try {
      const valuesToUse =
        pendingDuplicateGuardValues || {
          redemptionDateUTC: new Date(),
          active: true,
        };
      const overrideFormData = buildRedemptionFormData(valuesToUse, {
        duplicateGuardOverrideReason: reason,
      });
      const response = selectedRedemption
        ? await api(`v1/redemption/${selectedRedemption._id}`, "PATCH", overrideFormData, {
            "Content-Type": "multipart/form-data",
          })
        : await api("v1/redemption", "POST", overrideFormData, {
            "Content-Type": "multipart/form-data",
          });
      if (response?.data?.success) {
        setDuplicateGuardModalOpen(false);
        setDuplicateGuardError(null);
        setDuplicateGuardReason("");
        const updatedData =
          response.data.data?.redemption || response.data.data?.newRedemption;
        if (updatedData) callback(updatedData);
      } else {
        alert(response?.data?.details || response?.data?.message || "Override failed.");
      }
    } catch (err) {
      alert(err?.response?.data?.details || err?.response?.data?.message || "Override failed.");
    } finally {
      setDuplicateGuardLoading(false);
    }
  };

  const handleContinueAnyway = async () => {
    setSubscriptionOverrideLoading(true);
    try {
      const valuesToUse =
        pendingSubscriptionOverrideValues || {
          redemptionDateUTC: new Date(),
          active: true
        };

      const overrideFormData = buildRedemptionFormData(valuesToUse, {
        overrideSubscriptionNotFound: true
      });

      const response = selectedRedemption
        ? await api(
            `v1/redemption/${selectedRedemption._id}`,
            "PATCH",
            overrideFormData,
            { "Content-Type": "multipart/form-data" }
          )
        : await api(
            "v1/redemption",
            "POST",
            overrideFormData,
            { "Content-Type": "multipart/form-data" }
          );

      if (response?.data?.success) {
        var updatedData = null;

        if (selectedRedemption) {
          updatedData = response.data.data.redemption;
        } else {
          updatedData = response?.data?.data?.newRedemption || null;
        }

        if (!updatedData) {
          alert(response?.data?.message || "Redemption completed.");
          return;
        }

        const vehicle = await api(`v1/vehicle/${updatedData.vehicle}`, "GET");
        const product = await api(`v1/product/${updatedData.product}`, "GET");
        const merchantStore = updatedData.merchantStore
          ? await api(`v1/merchantStore/${updatedData.merchantStore}`, "GET")
          : null;
        const merchantUser = updatedData.merchantUser
          ? await api(`v1/merchantUser/${updatedData.merchantUser}`, "GET")
          : null;

        updatedData.vehicle = vehicle.data.data.vehicle;
        updatedData.product = product.data.data.product;
        updatedData.merchantStore = merchantStore
          ? merchantStore.data.data.merchantStore
          : null;
        updatedData.merchantUser = merchantUser
          ? merchantUser.data.data.merchantUser
          : null;

        callback(updatedData);
      } else {
        const errorMessage =
          response?.data?.message || "Redemption Failed";
        const errorDetails = response?.data?.details || "";
        const fullMessage = errorDetails
          ? `${errorMessage}\n\n${errorDetails}`
          : errorMessage;
        alert(fullMessage);
      }
    } finally {
      setSubscriptionOverrideLoading(false);
      setSubscriptionOverrideModalOpen(false);
    }
  };

  // Contract acceptance handlers removed - this is an admin form, not customer-facing
  // const handleContractAccepted = async (acceptedContract) => { ... }
  // const handleContractModalClose = () => { ... }

  const loadProductOptions = async (inputValue) => {
    try {
      const lower = (inputValue || "").toLowerCase();
      if (!lower) return productOptionsGrouped;
      const filtered = productOptionsGrouped
        .map((group) => ({
          label: group.label,
          options: group.options.filter((opt) => opt.label.toLowerCase().includes(lower))
        }))
        .filter((group) => group.options.length > 0);
      return filtered;
    } catch (error) {
      console.error("Error loading product options:", error);
      return [];
    }
  };

  const loadMerchantAccountOptions = async (inputValue) => {
    try {
      const filteredOptions = merchantAccountOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      return filteredOptions;
    } catch (error) {
      console.error("Error loading product options:", error);
      return [];
    }
  };
  const loadMerchantStoreOptions = async (inputValue) => {
    try {
      const filteredOptions = merchantStoreOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      return filteredOptions;
    } catch (error) {
      console.error("Error loading product options:", error);
      return [];
    }
  };
  const loadMerchantUserOptions = async (inputValue) => {
    console.log("Loaded merchantUserOptions Options", merchantUserOptions);
    try {
      const filteredOptions = merchantUserOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      return filteredOptions;
    } catch (error) {
      console.error("Error loading product options:", error);
      return [];
    }
  };

  const loadVehicleOptions = async (inputValue) => {
    try {
      const filteredOptions = vehicleOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      return filteredOptions;
    } catch (error) {
      console.error("Error loading Vehicle options:", error);
      return [];
    }
  };

  const handleProductChange = (selectedOptions) => {
    if (selectedOptions) {
      setProduct(selectedOptions);
      if (!selectedOptions.requiresVehicleVariant) setAddOnPriceVariant("");
    } else {
      setProduct(null);
      setAddOnPriceVariant("");
    }
  };
  const handleVehicleChange = (selectedOptions) => {
    if (selectedOptions) {
      setVehicle(selectedOptions);
    } else {
      setVehicle(null);
    }
  };

  const handleMerchantAccountChange = (selectedOptions) => {
    if (selectedOptions) {
      setMerchantAccount(selectedOptions);
    } else {
      setMerchantAccount(null);
    }
  };
  const handleMerchantStoreChange = (selectedOptions) => {
    if (selectedOptions) {
      setMerchantStore(selectedOptions);
    } else {
      setMerchantStore(null);
    }
  };
  const handleMerchantUserChange = (selectedOptions) => {
    if (selectedOptions) {
      setMerchantUser(selectedOptions);
    } else {
      setMerchantUser(null);
    }
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#9e9e9e",
      zIndex: 2
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 3
    })
  };

  // const DatePickerField = ({ name, value, onChange }) => {
  //     return (
  //         <DatePicker
  //             selected={(value && new Date(value)) || null}
  //             onChange={(val) => {
  //                 onChange(name, val);
  //             }}
  //         />
  //     );
  // };

  const validationSchema = Yup.object().shape({
    redemptionDateUTC: Yup.date()
      .nullable()
      // .min(new Date(), "Start Date must be later than today"),
      .required("Redemption Date is required")
    // redemptions: Yup.array()
    //     .of(Yup.date())
    //     .required("Redemptions is required"),
    // active: Yup.boolean().required("Active field is required"),
  });

  return (
    <>
      <Formik
        initialValues={{
          vehicle: selectedRedemption ? selectedRedemption.vehicle._id : "",
          product: selectedRedemption ? selectedRedemption.product._id : "",
          merchantStore:
            selectedRedemption && selectedRedemption.merchantStore ? selectedRedemption.merchantStore._id : "",
          merchantUser:
            selectedRedemption && selectedRedemption.merchantUser ? selectedRedemption.merchantUser._id : "",
          redemptionDateUTC: selectedRedemption ? new Date(selectedRedemption.redemptionDateUTC) : "",
          active: selectedRedemption ? selectedRedemption.active : true,
          createdOnUTC: selectedRedemption ? selectedRedemption.createdOnUTC : ""
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="form w-full flex flex-column justify-between priorityoneform form-redemption min-h-[20vh]">
            <div className="form-input-wrap flex gap-2 w-full">
              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="vehicle">
                      Vehicle:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="vehicle">
                      {({ field }) => (
                        <AsyncSelect
                          name="vehicle"
                          {...field}
                          cacheOptions
                          defaultOptions={vehicleOptions}
                          loadOptions={loadVehicleOptions}
                          styles={selectStyles}
                          onChange={handleVehicleChange}
                          value={vehicle}
                          formatOptionLabel={function (data) {
                            return <span className="capitalize" dangerouslySetInnerHTML={{ __html: data.label }} />;
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="vehicle" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="product">
                      Select Product:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="product">
                      {({ field }) => (
                        <AsyncSelect
                          name="product"
                          {...field}
                          cacheOptions
                          defaultOptions={productOptionsGrouped}
                          loadOptions={loadProductOptions}
                          formatGroupLabel={(group) => (
                            <span className="font-semibold text-gray-700">{group.label}</span>
                          )}
                          styles={selectStyles}
                          onChange={handleProductChange}
                          value={product}
                          placeholder="Search or select product..."
                        />
                      )}
                    </Field>
                    <ErrorMessage name="product" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              {product?.requiresVehicleVariant && (
                <div className="w-full flex gap-5">
                  <div className="flex flex-row justify-between form-group w-full">
                    <div className="w-full">
                      <label className="label" htmlFor="addOnPriceVariant">
                        Price variant:
                      </label>
                    </div>
                    <div className="w-full flex-column">
                      <select
                        id="addOnPriceVariant"
                        name="addOnPriceVariant"
                        value={addOnPriceVariant}
                        onChange={(e) => setAddOnPriceVariant(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 bg-white"
                      >
                        <option value="">-- Select price variant --</option>
                        {ADDON_PRICE_VARIANTS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-gray-500 text-sm mt-1 block">
                        Choose the vehicle type for tier pricing (Sedan, SUV/Wagon, or 4WD/7 Seater).
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="merchantAccount">
                      Select Merchant Account:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="merchantAccount">
                      {({ field }) => (
                        <AsyncSelect
                          name="merchantAccount"
                          {...field}
                          cacheOptions
                          defaultOptions={merchantAccountOptions}
                          loadOptions={loadMerchantAccountOptions}
                          styles={selectStyles}
                          onChange={handleMerchantAccountChange}
                          value={merchantAccount}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="merchantAccount" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="merchantStore">
                      Select Merchant Store:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="merchantStore">
                      {({ field }) => (
                        <AsyncSelect
                          name="merchantStore"
                          {...field}
                          cacheOptions
                          defaultOptions={merchantStoreOptions}
                          loadOptions={loadMerchantStoreOptions}
                          styles={selectStyles}
                          onChange={handleMerchantStoreChange}
                          value={merchantStore}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="merchantStore" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="merchantUser">
                      Select Merchant User:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="merchantUser">
                      {({ field }) => (
                        <AsyncSelect
                          name="merchantUser"
                          {...field}
                          cacheOptions
                          defaultOptions={merchantUserOptions}
                          loadOptions={loadMerchantUserOptions}
                          styles={selectStyles}
                          onChange={handleMerchantUserChange}
                          value={merchantUser}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="merchantUser" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="redemptionDateUTC">
                      Select Redemption Date (UTC):
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="redemptionDateUTC">
                      {({ field }) => (
                        <DatePicker
                          {...field}
                          disableToolbar
                          variant="inline"
                          format="dd/MM/yyyy"
                          margin="normal"
                          id="date-picker-redemptionDateUTC"
                          label="Select a redemptionDateUTC"
                          placeholderText="dd/mm/YYYY"
                          value={values.redemptionDateUTC}
                          KeyboardButtonProps={{
                            "aria-label": "change date"
                          }}
                          selected={values.redemptionDateUTC}
                          onChange={(date) => setFieldValue("redemptionDateUTC", date)}
                          disabled={isSubmitting}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="redemptionDateUTC" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="active">
                      Active:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <Field name="active" type="checkbox">
                      {({ field }) => (
                        <FormControlLabel
                          control={<Switch {...field} checked={field.value} />}
                          label={field.value ? "Active" : "Inactive"}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="active" component="div" className="error-message" />
                  </div>
                </div>
              </div>

              <div className="w-full flex gap-5">
                <div className="flex flex-row justify-between form-group w-full">
                  <div className="w-full">
                    <label className="label" htmlFor="vehicleImage">
                      Vehicle Image:
                    </label>
                  </div>

                  <div className="w-full flex-column">
                    <input
                      type="file"
                      id="vehicleImage"
                      name="vehicleImage"
                      required // Adding the required attribute
                      accept="image/*"
                      // capture
                      onChange={(e) => {
                        // Handle file upload logic here
                        const selectedFile = e.target.files[0];
                        setVehicleImage(selectedFile);
                        console.log("selectedFile", selectedFile);
                        console.log("vehicleImage", vehicleImage);
                        // You can perform actions with the selected file
                      }}
                    />
                    <ErrorMessage name="vehicleImage" component="div" className="error-message" />
                  </div>
                </div>
              </div>
            </div>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Button variant="contained" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Submit
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      {/* Duplicate guard override (admin-only) */}
      <ModalPopup
        showModal={duplicateGuardModalOpen}
        handleShowModal={() => {
          setDuplicateGuardModalOpen(false);
          setDuplicateGuardError(null);
          setDuplicateGuardReason("");
        }}
        popupTitle={duplicateGuardModalTitle(duplicateGuardError?.errorCode)}
        popupContent={
          <div className="p-4 space-y-3">
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm font-semibold text-red-900">
                {duplicateGuardError?.message || "Redemption blocked"}
              </p>
              <p className="text-sm text-red-800 mt-1 whitespace-pre-line">
                {duplicateGuardError?.details}
              </p>
            </div>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Administrator override reason (required)"
              placeholder="Explain why this duplicate redemption should be allowed (min. 10 characters)."
              value={duplicateGuardReason}
              onChange={(e) => setDuplicateGuardReason(e.target.value)}
              helperText="This reason is stored in the audit log."
            />
          </div>
        }
        cancelButtonText="Cancel"
        confirmButtonText={duplicateGuardLoading ? "Saving…" : "Override and continue"}
        confirmCallbackAction={
          duplicateGuardLoading ? () => {} : handleDuplicateGuardOverride
        }
      />

      {/* Subscription override modal (admin-only) */}
      <ModalPopup
        showModal={subscriptionOverrideModalOpen}
        handleShowModal={() => {
          setSubscriptionOverrideModalOpen(false);
          setSubscriptionOverrideError(null);
        }}
        popupTitle="Active Subscription Not Found"
        popupContent={
          <div className="p-4">
            <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm font-semibold text-yellow-900">
                Admin override requested
              </p>
              <p className="text-sm text-yellow-800 mt-1 whitespace-pre-line">
                {subscriptionOverrideError?.details ||
                  "No active subscription was found for the selected vehicle/product."}
              </p>
            </div>

            {subscriptionOverrideError?.data && (
              <div className="text-sm text-gray-700 space-y-1">
                {subscriptionOverrideError.data.vehicleRego && (
                  <p>
                    Vehicle rego:{" "}
                    <strong>{subscriptionOverrideError.data.vehicleRego}</strong>
                  </p>
                )}
                {subscriptionOverrideError.data.vehicleId && (
                  <p>
                    Vehicle ID:{" "}
                    <strong>{subscriptionOverrideError.data.vehicleId}</strong>
                  </p>
                )}
                {subscriptionOverrideError.data.productName && (
                  <p>
                    Product:{" "}
                    <strong>{subscriptionOverrideError.data.productName}</strong>
                  </p>
                )}
                {subscriptionOverrideError.data.productId && (
                  <p>
                    Product ID:{" "}
                    <strong>{subscriptionOverrideError.data.productId}</strong>
                  </p>
                )}
                {Array.isArray(subscriptionOverrideError?.data?.activeSubscriptionsForVehicle) &&
                  subscriptionOverrideError.data.activeSubscriptionsForVehicle.some(
                    (s) =>
                      s.productId === subscriptionOverrideError.data.productId &&
                      String(s.subscriptionDeleted || "")
                        .toLowerCase()
                        .includes("yes")
                  ) && (
                    <p className="text-amber-700">
                      Matching subscription is marked cancelled (`deleted: yes`).
                    </p>
                  )}
                {Array.isArray(subscriptionOverrideError.data.activeSubscriptionsForVehicle) &&
                  subscriptionOverrideError.data.activeSubscriptionsForVehicle.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium text-gray-800 mb-1">
                        Active subscription products for this vehicle
                      </p>
                      <div className="space-y-1">
                        {subscriptionOverrideError.data.activeSubscriptionsForVehicle
                          .slice(0, 5)
                          .map((s) => (
                            <p key={s.subscriptionId || s.productId || s.productName}>
                              {s.productName || "Unknown product"}{" "}
                              {s.productId ? (
                                <span className="text-gray-500">
                                  ({s.productId})
                                </span>
                              ) : null}
                              {String(s.subscriptionDeleted || "")
                                .toLowerCase()
                                .includes("yes") && (
                                <span className="text-red-600"> (deleted)</span>
                              )}
                            </p>
                          ))}
                      </div>
                    </div>
                  )}
                {subscriptionOverrideError.data.customerName && (
                  <p>
                    Customer:{" "}
                    <strong>{subscriptionOverrideError.data.customerName}</strong>
                  </p>
                )}
              </div>
            )}

            <p className="text-sm text-gray-600 mt-3">
              This admin override triggers when we cannot link a non-deleted active subscription to
              the selected <strong>vehicle + product</strong> combination.
              If there is a subscription that has been cancelled (marked <code>deleted: yes</code>),
              you can continue anyway. Please verify the customer and product details first.
            </p>
          </div>
        }
        cancelButtonText="Cancel"
        confirmButtonText={
          subscriptionOverrideLoading ? "Continuing..." : "Continue anyway"
        }
        confirmCallbackAction={
          subscriptionOverrideLoading ? () => {} : handleContinueAnyway
        }
      />

      {/* Contract Validation Modal for Merchants */}
      <ModalPopup
        showModal={contractValidationModalOpen}
        handleShowModal={() => {
          setContractValidationModalOpen(false);
          setContractValidationMessage("");
        }}
        popupTitle="Contract Signature Required"
        popupContent={
          <div className="p-6">
            <div className="mb-4">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Customer Contract Not Signed</h3>
              <p className="text-sm text-gray-600 text-center mb-4">{contractValidationMessage}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-blue-800">Next Steps:</h4>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Ask the customer to log into their Priority One customer portal</li>
                      <li>Have them sign their subscription contract</li>
                      <li>Once signed, you can process their redemption</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setContractValidationModalOpen(false);
                  setContractValidationMessage("");
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Understood
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default RedemptionForm;
